import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import type {
  AccessReview,
  Assessment,
  AuditEvent,
  CourseListItem,
  DpiaCoverage,
  MyPolicies,
  Paginated,
  PolicyAcceptanceMatrix,
  ProgressSummary,
  RecordAssignment,
  TrainingOrgProgress,
} from '@grc/types';
import { apiFetch, apiUrl } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import { matrixKey, myPoliciesKey } from '../policies/shared.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  ProgressBar,
  formatDate,
  formatDateTime,
} from '../../components/ui.js';
import '../assessments/assessments.css';
import './dashboard.css';

/** A completed course only counts while it has not expired. */
function isCurrent(progress: CourseListItem['progress']): boolean {
  return progress.status === 'completed' && !progress.expired;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function DashboardPage() {
  const { org } = useOrg();
  const { me } = useAuth();
  const isAdmin = org.role === 'admin';
  const isAssessor = org.role === 'assessor';
  const isReviewer = org.role === 'reviewer';
  const [logoBroken, setLogoBroken] = useState(false);
  // A 404 for one org's (unset) logo must not hide another org's logo after
  // switching — the dashboard stays mounted across org changes.
  useEffect(() => setLogoBroken(false), [org.orgId]);

  const assessments = useQuery({
    queryKey: ['assessments', org.orgId],
    queryFn: () => apiFetch<Assessment[]>(`/orgs/${org.orgId}/assessments`),
  });
  // Progress for every assessment: the grid shows only the first 8, but the
  // review-pipeline totals must cover the whole organisation.
  const progressQueries = useQueries({
    queries: (assessments.data ?? []).map((a) => ({
      queryKey: ['progress', a.id],
      queryFn: () => apiFetch<ProgressSummary>(`/assessments/${a.id}/progress`),
    })),
  });
  const progressPending = progressQueries.some((q) => q.isPending);
  const myCourses = useQuery({
    queryKey: ['courses', org.orgId],
    queryFn: () => apiFetch<{ items: CourseListItem[] }>(`/orgs/${org.orgId}/training/courses`),
    enabled: !isAssessor,
  });
  const teamTraining = useQuery({
    queryKey: ['training-progress', org.orgId],
    queryFn: () => apiFetch<TrainingOrgProgress>(`/orgs/${org.orgId}/training/progress`),
    enabled: isAdmin || isReviewer,
  });
  // Policy acceptance: the member's own list (same key as the My policies
  // page and the nav dot) and, for admins/reviewers, the org-wide matrix.
  const myPolicies = useQuery({
    queryKey: myPoliciesKey(org.orgId),
    queryFn: () => apiFetch<MyPolicies>(`/orgs/${org.orgId}/my-policies`),
    enabled: !isAssessor,
  });
  const policyMatrix = useQuery({
    queryKey: matrixKey(org.orgId),
    queryFn: () => apiFetch<PolicyAcceptanceMatrix>(`/orgs/${org.orgId}/policy-acceptance`),
    enabled: isAdmin || isReviewer,
  });
  const audit = useQuery({
    queryKey: ['audit-recent', org.orgId],
    queryFn: () =>
      apiFetch<Paginated<AuditEvent>>(`/orgs/${org.orgId}/audit`, { query: { limit: 15 } }),
    enabled: isAdmin,
  });
  // Same key as the coverage page, so a DPIA created there refreshes this card.
  const dpiaCoverage = useQuery({
    queryKey: ['records', org.orgId, 'dpia-coverage'],
    queryFn: () => apiFetch<DpiaCoverage>(`/orgs/${org.orgId}/dpia-coverage`),
    enabled: !isAssessor,
  });
  // Access reviews (admins and reviewers): same overdue key as the reviews
  // page so both read one cached response.
  const overdueGrants = useQuery({
    queryKey: ['record-assignments', org.orgId, 'overdue'],
    queryFn: () =>
      apiFetch<{ items: RecordAssignment[] }>(`/orgs/${org.orgId}/record-assignments`, {
        query: { overdue: true },
      }),
    enabled: isAdmin || isReviewer,
  });
  const accessReviews = useQuery({
    queryKey: ['access-reviews', org.orgId, 'dashboard'],
    queryFn: () =>
      apiFetch<Paginated<AccessReview>>(`/orgs/${org.orgId}/access-reviews`, {
        query: { limit: 50 },
      }),
    enabled: isAdmin || isReviewer,
  });

  // ---- access reviews rollup: the next cycle comes from the latest completed
  // or approved review, never from the grants themselves ----
  const accessReviewSummary = useMemo(() => {
    const reviews = accessReviews.data?.items ?? [];
    const finished = reviews
      .filter((r) => r.status !== 'in_progress')
      .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''));
    return {
      overdueGrants: overdueGrants.data?.items.length ?? 0,
      inProgress: reviews.filter((r) => r.status === 'in_progress').length,
      last: finished[0] ?? null,
    };
  }, [accessReviews.data, overdueGrants.data]);

  // ---- my training rollup (everyone except assessors) ----
  const myTraining = useMemo(() => {
    const items = myCourses.data?.items ?? [];
    const completed = items.filter((c) => isCurrent(c.progress));
    const expiringSoon = completed.filter(
      (c) =>
        c.progress.expiresAt &&
        new Date(c.progress.expiresAt).getTime() - Date.now() < THIRTY_DAYS_MS,
    );
    const inProgress = items.filter((c) => c.progress.status === 'in_progress');
    const expired = items.filter((c) => c.progress.expired);
    return {
      total: items.length,
      completed: completed.length,
      percent: items.length === 0 ? 0 : Math.round((completed.length / items.length) * 100),
      expiringSoon,
      inProgress,
      expired,
    };
  }, [myCourses.data]);

  // ---- org-wide training rollup (admins & reviewers) ----
  const orgTraining = useMemo(() => {
    if (!teamTraining.data) return null;
    const courseCount = teamTraining.data.courses.length;
    const members = teamTraining.data.members
      .filter((m) => m.role !== 'assessor')
      .map((m) => {
        const done = m.courses.filter((c) => c.status === 'completed' && !c.expired).length;
        return { ...m, done, percent: courseCount === 0 ? 0 : Math.round((done / courseCount) * 100) };
      });
    const fullyTrained = members.filter((m) => m.done === courseCount).length;
    const overall =
      members.length === 0
        ? 0
        : Math.round(members.reduce((sum, m) => sum + m.percent, 0) / members.length);
    const behind = [...members].sort((a, b) => a.percent - b.percent).filter((m) => m.done < courseCount);
    return { courseCount, members, fullyTrained, overall, behind };
  }, [teamTraining.data]);

  // ---- review pipeline (from the loaded assessment progress) ----
  const pipeline = useMemo(() => {
    const rows = (assessments.data ?? []).map((a, i) => ({
      assessment: a,
      progress: progressQueries[i]?.data,
    }));
    const loaded = rows.filter((r) => r.progress);
    return {
      underReview: loaded.reduce((sum, r) => sum + (r.progress!.byStatus.under_review ?? 0), 0),
      evidenceMissing: loaded.reduce((sum, r) => sum + r.progress!.evidenceMissing, 0),
      reviewerComments: loaded.reduce((sum, r) => sum + r.progress!.openReviewerComments, 0),
      queues: loaded
        .filter((r) => (r.progress!.byStatus.under_review ?? 0) > 0)
        .sort((a, b) => (b.progress!.byStatus.under_review ?? 0) - (a.progress!.byStatus.under_review ?? 0)),
    };
  }, [assessments.data, progressQueries]);

  if (assessments.isLoading) return <LoadingPane label="Loading your workspace" />;
  const items = assessments.data ?? [];

  const myTrainingCard = !isAssessor && (
    <Card title="My training">
      <div className="dash-stat">
        {myTraining.completed} / {myTraining.total}
      </div>
      <p className="dash-stat-hint">courses complete and in date</p>
      <ProgressBar percent={myTraining.percent} label="My training completion" />
      <div className="meta" style={{ marginTop: 10 }}>
        {myTraining.expired.length > 0 && (
          <Badge tone="danger">{myTraining.expired.length} expired — retake</Badge>
        )}
        {myTraining.expiringSoon.length > 0 && (
          <Badge tone="warning">{myTraining.expiringSoon.length} expiring soon</Badge>
        )}
        {myTraining.inProgress.length > 0 && (
          <Badge tone="info">{myTraining.inProgress.length} in progress</Badge>
        )}
      </div>
      <p className="dash-card-footer">
        <Link to="/training/my-courses">
          {myTraining.completed < myTraining.total ? 'Continue training →' : 'View my courses →'}
        </Link>
      </p>
    </Card>
  );

  const teamTrainingCard = (isAdmin || isReviewer) && orgTraining && (
    <Card title="Team training">
      <div className="dash-stat">{orgTraining.overall}%</div>
      <p className="dash-stat-hint">
        average completion · {orgTraining.fullyTrained} of {orgTraining.members.length} staff fully
        trained
      </p>
      {orgTraining.behind.length > 0 ? (
        <ul
          className="dash-list dash-scroll"
          tabIndex={0}
          aria-label="Staff with outstanding training"
        >
          {orgTraining.behind.slice(0, 6).map((m) => (
            <li key={m.userId}>
              <span className="dash-list-name">{m.name}</span>
              <span className="dash-list-value">
                {m.done}/{orgTraining.courseCount}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          Everyone is up to date. 🎉
        </p>
      )}
      <p className="dash-card-footer">
        <Link to="/training/track">Track training →</Link>
      </p>
    </Card>
  );

  const openPolicies = (myPolicies.data?.items ?? []).filter((p) => p.status.status !== 'accepted');
  const myPoliciesCard = !isAssessor && myPolicies.data && myPolicies.data.items.length > 0 && (
    <Card title="My policies">
      <div className="dash-stat">{openPolicies.length}</div>
      <p className="dash-stat-hint">
        {openPolicies.length === 1 ? 'policy needs' : 'policies need'} your acceptance
        {myPolicies.data.counts.overdue > 0 && ` · ${myPolicies.data.counts.overdue} overdue`}
      </p>
      {openPolicies.length > 0 ? (
        <ul className="dash-list dash-scroll" tabIndex={0} aria-label="Policies awaiting your acceptance">
          {openPolicies.slice(0, 3).map((p) => (
            <li key={p.releaseId}>
              <span className="dash-list-name">
                <Link to={`/policies/${p.releaseId}`}>{p.title}</Link>
              </span>
              <span className="dash-list-value">
                {p.status.status === 'overdue' ? 'overdue · ' : 'due '}
                {formatDate(p.status.dueOn)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          You have accepted every published policy. 🎉
        </p>
      )}
      <p className="dash-card-footer">
        <Link to="/policies">{openPolicies.length > 0 ? 'Read and accept →' : 'My policies →'}</Link>
      </p>
    </Card>
  );

  const matrixCounts = policyMatrix.data?.counts;
  const policyPercent =
    matrixCounts && matrixCounts.total > 0 ? Math.round((matrixCounts.accepted / matrixCounts.total) * 100) : 0;
  const policyAcceptanceCard = (isAdmin || isReviewer) && matrixCounts && matrixCounts.total > 0 && (
    <Card title="Policy acceptance">
      <div className="dash-stat">{policyPercent}%</div>
      <p className="dash-stat-hint">
        accepted across {policyMatrix.data!.policies.length}{' '}
        {policyMatrix.data!.policies.length === 1 ? 'policy' : 'policies'} and{' '}
        {policyMatrix.data!.members.length} {policyMatrix.data!.members.length === 1 ? 'person' : 'people'}
      </p>
      <ProgressBar percent={policyPercent} label="Policy acceptance" />
      <div className="meta" style={{ marginTop: 10 }}>
        {matrixCounts.overdue > 0 && <Badge tone="danger">{matrixCounts.overdue} overdue</Badge>}
        {matrixCounts.outstanding > 0 && <Badge tone="warning">{matrixCounts.outstanding} outstanding</Badge>}
        {matrixCounts.overdue === 0 && matrixCounts.outstanding === 0 && (
          <Badge tone="success">Everyone is up to date</Badge>
        )}
      </div>
      <p className="dash-card-footer">
        <Link to="/policies/tracking">Track acceptance →</Link>
        {' · '}
        <Link to="/policies/register">Document register →</Link>
      </p>
    </Card>
  );

  const pipelineCard = (isAdmin || isReviewer) && (
    <Card title={isReviewer ? 'Awaiting your review' : 'Review pipeline'}>
      <div className="dash-stat">{progressPending ? '…' : pipeline.underReview}</div>
      <p className="dash-stat-hint">
        {progressPending ? (
          'counting responses awaiting review'
        ) : (
          <>
            response{pipeline.underReview === 1 ? '' : 's'} awaiting review
            {pipeline.evidenceMissing > 0 && ` · ${pipeline.evidenceMissing} missing evidence`}
          </>
        )}
      </p>
      {progressPending ? null : pipeline.queues.length > 0 ? (
        <ul
          className="dash-list dash-scroll"
          tabIndex={0}
          aria-label="Assessments with responses awaiting review"
        >
          {pipeline.queues.slice(0, 6).map((r) => (
            <li key={r.assessment.id}>
              <span className="dash-list-name">
                <Link to={`/assessments/${r.assessment.id}/review`}>{r.assessment.name}</Link>
              </span>
              <span className="dash-list-value">{r.progress!.byStatus.under_review}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
          Nothing waiting — the queue is clear.
        </p>
      )}
      <p className="dash-card-footer">
        <Link to="/assessments">All assessments →</Link>
      </p>
    </Card>
  );

  // Only once at least one software item or supplier is flagged as a cloud service.
  const dpiaCounts = dpiaCoverage.data?.counts;
  const dpiaCard = !isAssessor && dpiaCounts && dpiaCounts.total > 0 && (
    <Card title="DPIA coverage">
      <div className="dash-stat">{dpiaCounts.missing}</div>
      <p className="dash-stat-hint">
        cloud service{dpiaCounts.missing === 1 ? '' : 's'} without a DPIA · {dpiaCounts.total} in
        scope
      </p>
      <div className="meta">
        {dpiaCounts.reviewOverdue > 0 && (
          <Badge tone="warning">{dpiaCounts.reviewOverdue} review overdue</Badge>
        )}
        {dpiaCounts.pending > 0 && <Badge tone="info">{dpiaCounts.pending} pending</Badge>}
        {dpiaCounts.covered > 0 && <Badge tone="success">{dpiaCounts.covered} covered</Badge>}
      </div>
      <p className="dash-card-footer">
        <Link to="/registers/dpias/coverage">DPIA coverage →</Link>
      </p>
    </Card>
  );

  const nextReviewOn = accessReviewSummary.last?.nextReviewOn ?? null;
  const nextReviewPast = nextReviewOn !== null && nextReviewOn < new Date().toISOString().slice(0, 10);
  const accessReviewCard = (isAdmin || isReviewer) && (
    <Card title="Access reviews">
      <div className="dash-stat">
        {overdueGrants.isPending ? '…' : accessReviewSummary.overdueGrants}
      </div>
      <p className="dash-stat-hint">
        grant{accessReviewSummary.overdueGrants === 1 ? '' : 's'} overdue for review
        {accessReviewSummary.inProgress > 0 &&
          ` · ${accessReviewSummary.inProgress} review${accessReviewSummary.inProgress === 1 ? '' : 's'} in progress`}
      </p>
      <div className="meta">
        {nextReviewOn ? (
          <Badge tone={nextReviewPast ? 'warning' : 'info'}>
            Next review due {formatDate(nextReviewOn)}
          </Badge>
        ) : (
          <Badge tone="neutral">No review cycle set yet</Badge>
        )}
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', marginTop: 10 }}>
        {accessReviewSummary.last ? (
          <>
            Last completed:{' '}
            <Link to={`/assets/software/access-reviews/${accessReviewSummary.last.id}`}>
              {accessReviewSummary.last.name}
            </Link>{' '}
            · {formatDate(accessReviewSummary.last.completedAt)}
          </>
        ) : (
          'No review completed yet — start one to re-certify who has access to what.'
        )}
      </p>
      <p className="dash-card-footer">
        <Link to="/assets/software/access-reviews">Access reviews →</Link>
      </p>
    </Card>
  );

  const activityCard = isAdmin && (audit.data?.items.length ?? 0) > 0 && (
    <Card title="Recent activity">
      <ul className="dash-list dash-scroll" tabIndex={0} aria-label="Recent audit events">
        {audit.data!.items.map((e) => (
          <li key={e.id}>
            <span className="dash-list-name">
              <Badge tone="neutral">{e.action}</Badge>{' '}
              <span style={{ color: 'var(--color-text-muted)' }}>{e.userName ?? 'system'}</span>
            </span>
            <span className="dash-list-value">{formatDateTime(e.createdAt)}</span>
          </li>
        ))}
      </ul>
      <p className="dash-card-footer">
        <Link to="/audit">Full audit log →</Link>
      </p>
    </Card>
  );

  const assessmentsGrid =
    items.length === 0 ? (
      <EmptyState
        title="No assessments yet"
        message={
          isAdmin
            ? 'Create an assessment from a standard to generate your compliance workspace, then draft responses from your shared corpus.'
            : 'Once an admin creates an assessment it will appear here.'
        }
        action={
          isAdmin ? (
            <Link to="/assessments">
              <Button>Create your first assessment</Button>
            </Link>
          ) : undefined
        }
      />
    ) : (
      <div className="assessment-grid">
        {items.slice(0, 8).map((a, i) => {
          const progress = progressQueries[i]?.data;
          return (
            <Card key={a.id} className="assessment-card">
              <h3>
                <Link to={`/assessments/${a.id}`}>{a.name}</Link>
              </h3>
              <div className="meta">
                <Badge tone="primary">{a.standardName}</Badge>
                <Badge>{a.versionLabel}</Badge>
                <Badge tone={a.status === 'open' ? 'info' : 'neutral'}>{a.status}</Badge>
              </div>
              {progress && (
                <>
                  <div className="progress-row">
                    <ProgressBar percent={progress.completionPercent} label={`Progress of ${a.name}`} />
                    <strong>{progress.completionPercent}%</strong>
                  </div>
                  <div className="meta" style={{ marginTop: 8 }}>
                    {progress.evidenceMissing > 0 && (
                      <Badge tone="warning">{progress.evidenceMissing} evidence missing</Badge>
                    )}
                    {progress.openReviewerComments > 0 && (
                      <Badge tone="danger">{progress.openReviewerComments} review comments</Badge>
                    )}
                    {progress.openAssessorComments > 0 && (
                      <Badge tone="purple">{progress.openAssessorComments} assessor comments</Badge>
                    )}
                    {(progress.byStatus.under_review ?? 0) > 0 && (
                      <Link to={`/assessments/${a.id}/review`}>
                        {progress.byStatus.under_review} awaiting review
                      </Link>
                    )}
                  </div>
                </>
              )}
            </Card>
          );
        })}
      </div>
    );

  const quickLinks = (
    <div className="dash-grid">
      {/* Contributors' own training leads their quick row; admins and
          reviewers already have it in the organisation row above. */}
      {!isAdmin && !isReviewer && myPoliciesCard}
      {!isAdmin && !isReviewer && myTrainingCard}
      {!isAdmin && !isReviewer && dpiaCard}
      {!isAssessor && (
        <Card title="Shared corpus">
          <p style={{ color: 'var(--color-text-muted)' }}>
            Maintain one set of policies, registers and processes, and reuse it across every
            standard you certify against.
          </p>
          <p className="dash-card-footer">
            <Link to="/corpus">Open the corpus →</Link>
          </p>
        </Card>
      )}
      <Card title="Standards catalogue">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Browse the clause structures and question sets for every supported standard.
        </p>
        <p className="dash-card-footer">
          <Link to="/standards">Browse standards →</Link>
        </p>
      </Card>
      <Card title="API & integrations">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Everything in this app is available over a documented REST API with org-scoped tokens
          and webhooks.
        </p>
        <p className="dash-card-footer">
          <a href={apiUrl('/docs')} target="_blank" rel="noreferrer">
            Open API docs →
          </a>
        </p>
      </Card>
    </div>
  );

  return (
    <div className="dashboard">
      <PageHeader
        title={`Welcome back, ${me?.user.name?.split(' ')[0] ?? 'there'}`}
        subtitle={`${org.orgName} · you are ${org.role === 'admin' ? 'an' : 'a'} ${org.role}`}
        actions={
          !logoBroken ? (
            <img
              className="dash-org-logo"
              src={apiUrl(`/orgs/${org.orgId}/logo`)}
              alt={`${org.orgName} logo`}
              onError={() => setLogoBroken(true)}
            />
          ) : undefined
        }
      />

      {/* Role-first: admins see the organisation pulse before anything else
          (team training, review pipeline, activity, then their own training);
          reviewers lead with their queue; contributors lead with their own
          training (in the quick row below the assessments). */}
      {(isAdmin || isReviewer) && (
        <div className="dash-grid">
          {isReviewer ? (
            <>
              {pipelineCard}
              {policyAcceptanceCard}
              {teamTrainingCard}
              {dpiaCard}
              {accessReviewCard}
              {myPoliciesCard}
              {myTrainingCard}
            </>
          ) : (
            <>
              {policyAcceptanceCard}
              {teamTrainingCard}
              {pipelineCard}
              {dpiaCard}
              {accessReviewCard}
              {activityCard}
              {myPoliciesCard}
              {myTrainingCard}
            </>
          )}
        </div>
      )}

      {assessmentsGrid}

      {quickLinks}
    </div>
  );
}
